import React, { useEffect, useState } from "react";
import { useLocation } from "react-router";
import classnames from "classnames";
import { Form, Formik } from "formik";
import { object, string } from "yup";
import {
    colors,
    ColorToken,
    DefaultSelectedOption,
    DialogBody,
    DialogFooter,
    DraggableDialog,
    FormTextField,
    GeneralIcon,
    PlanrButton,
    PlanrButtonProps,
    PlanrDatePicker,
    PlanrDialog,
    PlanrInput,
    PlanrMultiSelect,
    PlanrSearch,
    PlanrTag,
    PlanrTagType,
    PlanrTextArea,
    PlanRDndUploader,
    renderSingleOption,
    SelectButton,
    SimpleSelect,
    space,
    SpaceToken,
    Stack,
    StandardFormButtons,
} from "modules/common/components/ui";
import { onlyDigitsInputKeyPress } from "modules/common/services/form/values";
import { MobileSection } from "./MobileSection";
import styles from "./UiKitPage.module.scss";

/**
 * Витрина UI-кита (`/ui-kit`): все компоненты из `modules/common/components/ui`
 * в живом виде. Страница для разработчиков, в меню её нет.
 *
 * Новый компонент кита добавляется сюда в том же коммите — по витрине видно,
 * что у нас уже есть, прежде чем писать своё. Компоненты мобильной версии
 * (`ui/mobile`, без Blueprint) — в секции «Мобильные», в светлой и тёмной теме.
 */
export const UiKitPage = () => {
    const { hash } = useLocation();

    // на секцию можно дать ссылку: /ui-kit#buttons
    useEffect(() => {
        hash && document.getElementById(hash.slice(1))?.scrollIntoView();
    }, [hash]);

    return (
        <div className={styles.page}>
            {/* верхняя полоса общая с блоком пользователя справа — он лежит поверх
                страницы, поэтому прокручивается только то, что ниже неё */}
            <header className={styles.header}>
                <h1 className={styles.title}>UI-кит</h1>
            </header>

            <div className={styles.content}>
                <p className={styles.lead}>
                    Все компоненты импортируются из одного места:{" "}
                    <code>import {"{ … }"} from "modules/common/components/ui"</code>. Если нужного элемента здесь
                    нет — сначала добавьте его в кит, потом используйте на экране.
                </p>

                <ColorsSection />
                <SpacingSection />
                <ButtonsSection />
                <IconsSection />
                <TagsSection />
                <InputsSection />
                <FormSection />
                <DialogsSection />
                <MobileSection />
            </div>
        </div>
    );
};

const Section = ({ id, title, code, children }: SectionProps) => (
    <section id={id} className={styles.section}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        {code && <pre className={styles.code}>{code}</pre>}
        {children}
    </section>
);

interface SectionProps {
    id: string;
    title: string;
    code?: string;
    children?: React.ReactNode;
}

const ColorsSection = () => (
    <Section
        id="colors"
        title="Цвета"
        code={`style={{ color: colors.danger }}   // TS\ncolor: $planr-red-color;           // SCSS`}
    >
        <div className={styles.swatches}>
            {(Object.keys(colors) as ColorToken[]).map((name) => (
                <div key={name} className={styles.swatch}>
                    <div className={styles.swatchColor} style={{ background: colors[name] }} />
                    <code>{name}</code>
                </div>
            ))}
        </div>
    </Section>
);

const SpacingSection = () => (
    <Section id="spacing" title="Отступы и раскладка" code={`<Stack gap="s" align="center">…</Stack>`}>
        <Stack direction="column" gap="s">
            {(Object.keys(space) as SpaceToken[])
                .filter((name) => name !== "none")
                .map((name) => (
                    <Stack key={name} gap="m" align="center">
                        <code className={styles.spaceName}>{name}</code>
                        <div className={styles.spaceBar} style={{ width: space[name] }} />
                    </Stack>
                ))}
        </Stack>
    </Section>
);

const buttonTypes: PlanrButtonProps["type"][] = [
    "primary",
    "secondary",
    "dashed",
    "neutral",
    "graybtn",
    "blueish",
    "lightblueish",
    "greenish",
    "lightgreenish",
    "danger",
    "lightdanger",
    "whiteish",
];

// big — кнопка-иконка с кеглем 50px, с текстом её не используют
const buttonSizes: NonNullable<PlanrButtonProps["size"]>[] = ["medium", "small", "tiny"];

const ButtonsSection = () => (
    <Section
        id="buttons"
        title="Кнопки"
        code={`<PlanrButton type="primary" size="small" icon="general-plus" onClick={add}>Добавить</PlanrButton>`}
    >
        <Stack direction="column" gap="m">
            {buttonTypes.map((type) => (
                <Stack key={type} gap="s" align="center" wrap>
                    <code className={styles.rowLabel}>{type}</code>
                    {buttonSizes.map((size) => (
                        <PlanrButton key={size} type={type} size={size}>
                            {size}
                        </PlanrButton>
                    ))}
                    <PlanrButton type={type} icon="general-plus">
                        С иконкой
                    </PlanrButton>
                    <PlanrButton type={type} icon="general-edit" round title="Круглая" />
                    <PlanrButton type={type} disabled>
                        Недоступна
                    </PlanrButton>
                </Stack>
            ))}
        </Stack>
    </Section>
);

/** Имена иконок читаются из спрайта на странице, так что список не устаревает. */
const useSpriteIcons = () => {
    const [icons, setIcons] = useState<string[]>([]);

    useEffect(() => {
        const ids = Array.from(document.querySelectorAll('symbol[id^="general-"]'), (node) => node.id);
        setIcons(Array.from(new Set(ids)).sort());
    }, []);

    return icons;
};

const IconsSection = () => {
    const icons = useSpriteIcons();
    const [filter, setFilter] = useState("");
    const visible = icons.filter((icon) => icon.includes(filter.trim()));

    return (
        <Section id="icons" title={`Иконки (${icons.length})`} code={`<GeneralIcon type="general-trash" size={16} />`}>
            <PlanrInput
                className={styles.iconFilter}
                placeholder="Поиск по имени"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
            />
            <div className={styles.icons}>
                {visible.map((icon) => (
                    <div key={icon} className={styles.icon} title={icon}>
                        <GeneralIcon type={icon} size={20} />
                        <span>{icon.replace("general-", "")}</span>
                    </div>
                ))}
            </div>
        </Section>
    );
};

const tagTypes: PlanrTagType[] = ["neutral", "primary", "success", "danger"];

const TagsSection = () => (
    <Section id="tags" title="Теги" code={`<PlanrTag type="success" uppercase>Оплачен</PlanrTag>`}>
        <Stack direction="column" gap="s">
            <Stack gap="s">
                {tagTypes.map((type) => (
                    <PlanrTag key={type} type={type}>
                        {type}
                    </PlanrTag>
                ))}
            </Stack>
            <Stack gap="s">
                {tagTypes.map((type) => (
                    <PlanrTag key={type} type={type} uppercase>
                        {type}
                    </PlanrTag>
                ))}
            </Stack>
        </Stack>
    </Section>
);

const selectItems: SelectItem[] = [
    { id: "1", label: "Первый вариант" },
    { id: "2", label: "Второй вариант" },
    { id: "3", label: "Третий вариант" },
];

const people: SelectItem[] = [
    "Иванов К.В.",
    "Петрова А.С.",
    "Сидоров М.И.",
    "Кузнецова Д.М.",
    "Смирнов А.А.",
    "Волкова Е.Н.",
].map((label, i) => ({ id: String(i + 1), label }));

const InputsSection = () => {
    const [text, setText] = useState("");
    const [query, setQuery] = useState("");
    const [filledQuery, setFilledQuery] = useState("Иванов");
    const [team, setTeam] = useState<string[]>(["1", "3"]);
    const [single, setSingle] = useState<string[]>(["2"]);
    const [longText, setLongText] = useState("Многострочный текст растёт\nпо высоте вслед за содержимым");
    const [date, setDate] = useState<Date | null>(new Date());
    const [selected, setSelected] = useState<SelectItem | null>(selectItems[0]);
    const [fileName, setFileName] = useState("");

    return (
        <Section
            id="inputs"
            title="Поля ввода"
            code={`<PlanrInput value={value} onChange={…} />\n<PlanrSearch query={query} onSearch={setQuery} />\n<PlanrTextArea value={value} onChange={…} />\n<PlanrDatePicker value={date} onChange={setDate} />\n<SimpleSelect … disabled><SelectButton text="Значение" /></SimpleSelect>   // только чтение\n<PlanrMultiSelect what="Ответственный" many="Ответственных" items={people} value={ids} onChange={setIds} />`}
        >
            <div className={styles.fields}>
                <Labeled label="PlanrInput">
                    <PlanrInput placeholder="Введите текст" value={text} onChange={(e) => setText(e.target.value)} />
                </Labeled>
                <Labeled label="PlanrSearch">
                    <PlanrSearch query={query} onSearch={setQuery} />
                </Labeled>
                <Labeled label="PlanrSearch — с текстом, × очищает">
                    <PlanrSearch query={filledQuery} onSearch={setFilledQuery} />
                </Labeled>
                <Labeled label='PlanrInput leftIcon'>
                    <PlanrInput placeholder="Иконка слева по центру" leftIcon="search" />
                </Labeled>
                <Labeled label='PlanrInput variant="tiny"'>
                    <PlanrInput variant="tiny" placeholder="Компактное поле" />
                </Labeled>
                <Labeled label="PlanrInput disabled">
                    <PlanrInput value="Нельзя изменить" disabled />
                </Labeled>
                <Labeled label="PlanrTextArea">
                    <PlanrTextArea value={longText} onChange={(e) => setLongText(e.target.value)} />
                </Labeled>
                <Labeled label="PlanrDatePicker">
                    <PlanrDatePicker value={date} onChange={setDate} />
                </Labeled>
                <Labeled label="SimpleSelect — один вариант">
                    <SimpleSelect
                        className="full-width-select"
                        activeItem={selected}
                        items={selectItems}
                        itemRenderer={renderSingleOption}
                        filterable={false}
                        onItemSelect={setSelected}
                    >
                        <DefaultSelectedOption option={selected} />
                    </SimpleSelect>
                </Labeled>
                <Labeled label="SimpleSelect + onClear — необязательное поле, × сбрасывает значение">
                    <SimpleSelect
                        className="full-width-select"
                        activeItem={selected}
                        items={selectItems}
                        itemRenderer={renderSingleOption}
                        filterable={false}
                        onItemSelect={setSelected}
                    >
                        <DefaultSelectedOption option={selected} onClear={() => setSelected(null)} />
                    </SimpleSelect>
                </Labeled>
                <Labeled label="SimpleSelect disabled + SelectButton (только чтение)">
                    <SimpleSelect
                        className="full-width-select"
                        activeItem={selected}
                        items={selectItems}
                        itemRenderer={renderSingleOption}
                        filterable={false}
                        onItemSelect={setSelected}
                        disabled
                    >
                        <SelectButton text={selected?.label ?? "Не выбрано"} />
                    </SimpleSelect>
                </Labeled>
                <Labeled label="PlanrMultiSelect — несколько значений">
                    <PlanrMultiSelect
                        what="Ответственный"
                        many="Ответственных"
                        items={people}
                        value={team}
                        onChange={setTeam}
                        placeholder="Поиск сотрудника"
                    />
                </Labeled>
                <Labeled label="PlanrMultiSelect — одно выбрано, highlight (фильтр)">
                    <PlanrMultiSelect
                        what="Исполнитель"
                        many="Исполнителей"
                        items={people}
                        value={single}
                        onChange={setSingle}
                        placeholder="Поиск сотрудника"
                        highlight
                    />
                </Labeled>
                <Labeled label="PlanRDndUploader">
                    <PlanRDndUploader
                        accept="*"
                        onFileSelected={async (file) => setFileName(file.name)}
                        what={fileName || undefined}
                    />
                </Labeled>
            </div>
        </Section>
    );
};

const Labeled = ({ label, wide, children }: { label: string; wide?: boolean; children: React.ReactNode }) => (
    // div, а не label: клик по label браузер передаёт первой кнопке внутри —
    // у мультивыбора это была кнопка удаления тега, и любой клик убирал значение
    <div className={classnames(styles.labeled, { [styles.wide]: wide })}>
        <code>{label}</code>
        {children}
    </div>
);

const digits = onlyDigitsInputKeyPress();

const demoSchema = object({
    name: string().label("Название").required(),
    email: string().label("Почта").email(),
    inn: string().label("ИНН"),
    comment: string().label("Комментарий"),
});

const FormSection = () => (
    <Section
        id="form"
        title="Форма"
        code={`<FormTextField name={fields.name} schema={schema} />\n<FormTextField name={fields.inn} schema={schema} onKeyPress={digits} maxLength={12} />\n<FormTextField name={fields.comment} schema={schema} multiline rows={3} />`}
    >
        <Formik
            initialValues={{ name: "", email: "", inn: "", comment: "" }}
            validationSchema={demoSchema}
            onSubmit={async () => {
                await new Promise((resolve) => setTimeout(resolve, 1000));
            }}
        >
            {(formProps) => (
                <Form autoComplete="off" className={styles.form}>
                    <FormTextField name="name" schema={demoSchema} placeholder="Обязательное поле" />
                    <FormTextField name="email" schema={demoSchema} type="email" />
                    <FormTextField name="inn" schema={demoSchema} onKeyPress={digits} maxLength={12} />
                    <FormTextField name="comment" schema={demoSchema} multiline rows={3} />
                    <StandardFormButtons
                        {...formProps}
                        what="пример"
                        isNewlyCreated={true}
                        isRemoved={false}
                        onRemove={() => undefined}
                    />
                </Form>
            )}
        </Formik>
    </Section>
);

const DialogsSection = () => {
    const [dialog, setDialog] = useState(false);
    const [draggable, setDraggable] = useState(false);

    return (
        <Section
            id="dialogs"
            title="Окна"
            code={`<PlanrDialog isOpen={isOpen} onClose={close} title="Заголовок" width={480}>\n    <DialogBody>…</DialogBody>\n    <DialogFooter>…</DialogFooter>\n</PlanrDialog>`}
        >
            <Stack gap="s">
                <PlanrButton type="secondary" onClick={() => setDialog(true)}>
                    PlanrDialog
                </PlanrButton>
                <PlanrButton type="secondary" onClick={() => setDraggable(true)}>
                    DraggableDialog
                </PlanrButton>
            </Stack>

            <PlanrDialog isOpen={dialog} onClose={() => setDialog(false)} title="Пример окна" width={480}>
                <DialogBody>
                    <Stack direction="column" gap="m">
                        <span>Окно в стиле planr с настройками по умолчанию.</span>
                        <PlanrInput placeholder="Поле внутри окна" />
                    </Stack>
                </DialogBody>
                <DialogFooter>
                    <PlanrButton type="secondary" onClick={() => setDialog(false)}>
                        Отмена
                    </PlanrButton>
                    <PlanrButton type="primary" onClick={() => setDialog(false)}>
                        Готово
                    </PlanrButton>
                </DialogFooter>
            </PlanrDialog>

            <DraggableDialog
                isOpen={draggable}
                onClose={() => setDraggable(false)}
                title="Окно тянется за шапку"
                width={400}
                height={200}
            >
                <DialogBody>Перетаскиваемое окно без затемнения — работа со страницей не блокируется.</DialogBody>
            </DraggableDialog>
        </Section>
    );
};
